import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import type { DealerGroup, GroupAggregate } from '../../types';

const GRADE_COLORS: Record<string, { bg: string; text: string; light: string }> = {
  A: { bg: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', light: 'bg-emerald-50 dark:bg-emerald-500/10' },
  B: { bg: 'bg-primary', text: 'text-primary', light: 'bg-primary/10' },
  C: { bg: 'bg-amber-400', text: 'text-amber-600 dark:text-amber-400', light: 'bg-amber-50 dark:bg-amber-400/10' },
  D: { bg: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400', light: 'bg-orange-50 dark:bg-orange-500/10' },
  F: { bg: 'bg-red-500', text: 'text-red-600 dark:text-red-400', light: 'bg-red-50 dark:bg-red-500/10' },
};

const SIGNAL_LABELS: Record<string, string> = {
  vehicleZoneCoverage: 'Zone Coverage',
  salespersonOnCamera: 'On Camera',
  lightingQuality: 'Lighting',
  cameraStability: 'Stability',
  videoDuration: 'Duration',
  speechClarity: 'Speech Pace',
  energyEnthusiasm: 'Energy',
  buyerNameMentioned: 'Buyer Name',
  featureCalloutCount: 'Feature Callout',
  callToActionPresent: 'CTA',
  zoneCoverage: 'Zone Coverage',
  faceOnCamera: 'On Camera',
  lighting: 'Lighting',
  stability: 'Stability',
  duration: 'Duration',
  speechPace: 'Speech Pace',
  energy: 'Energy',
  buyerName: 'Buyer Name',
  featureCallout: 'Feature Callout',
  cta: 'CTA',
};

const SIGNAL_ICONS: Record<string, string> = {
  vehicleZoneCoverage: 'directions_car',
  salespersonOnCamera: 'person',
  lightingQuality: 'light_mode',
  cameraStability: 'videocam',
  videoDuration: 'timer',
  speechClarity: 'record_voice_over',
  energyEnthusiasm: 'bolt',
  buyerNameMentioned: 'badge',
  featureCalloutCount: 'star',
  callToActionPresent: 'campaign',
  zoneCoverage: 'directions_car',
  faceOnCamera: 'person',
  lighting: 'light_mode',
  stability: 'videocam',
  duration: 'timer',
  speechPace: 'record_voice_over',
  energy: 'bolt',
  buyerName: 'badge',
  featureCallout: 'star',
  cta: 'campaign',
};

function scoreColor(score: number): string {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-primary';
  if (score >= 40) return 'bg-amber-400';
  return 'bg-red-500';
}

function scoreTextColor(score: number): string {
  if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 60) return 'text-primary';
  if (score >= 40) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

interface Alert {
  type: 'error' | 'warning' | 'info';
  icon: string;
  message: string;
}

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: string;
  accent?: string;
  sub?: string;
  onClick?: () => void;
}> = ({ label, value, icon, accent = 'text-primary', sub, onClick }) => (
  <div
    className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6 flex flex-col gap-4 transition-all ${
      onClick ? 'cursor-pointer hover:shadow-md hover:border-primary/30 hover:scale-[1.02] active:scale-[0.99]' : ''
    }`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
        {label}
      </p>
      <span className={`material-symbols-outlined text-[22px] ${accent} opacity-70`}>{icon}</span>
    </div>
    <div>
      <p className={`text-3xl font-black tracking-tight ${accent}`}>{value}</p>
      {sub && <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 mt-1">{sub}</p>}
    </div>
  </div>
);

const AlertBanner: React.FC<{ alert: Alert }> = ({ alert }) => {
  const styles = {
    error: 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800/30 text-red-700 dark:text-red-400',
    warning: 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/30 text-amber-700 dark:text-amber-400',
    info: 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/30 text-blue-700 dark:text-blue-400',
  };
  return (
    <div className={`flex items-center gap-3 px-5 py-3.5 border rounded-xl text-sm font-semibold ${styles[alert.type]}`}>
      <span className="material-symbols-outlined text-[18px] shrink-0">{alert.icon}</span>
      {alert.message}
    </div>
  );
};

const GroupAdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [group, setGroup] = useState<DealerGroup | null>(null);
  const [stats, setStats] = useState<GroupAggregate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [groupRes, statsRes] = await Promise.all([
          api.get('/group-admin/me'),
          api.get('/group-admin/benchmarks'),
        ]);
        setGroup(groupRes.data.data);
        setStats(statsRes.data.data);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const totalVideosWithGrade = stats
    ? Object.values(stats.gradeDistribution).reduce((a, b) => a + b, 0)
    : 0;

  const unscoredVideos = stats?.pendingCount ?? 0;

  const maxGradeCount = stats
    ? Math.max(...Object.values(stats.gradeDistribution), 1)
    : 1;

  const alerts: Alert[] = [];
  if (stats) {
    if (stats.averageScore < 40 && stats.totalVideos > 0) {
      alerts.push({ type: 'error', icon: 'warning', message: `Critical: Group average score is ${stats.averageScore.toFixed(1)} — F grade. Immediate coaching intervention recommended.` });
    }
    if (stats.complianceFlagRate > 0.25 && stats.totalVideos > 0) {
      alerts.push({ type: 'warning', icon: 'flag', message: `Elevated compliance risk: ${(stats.complianceFlagRate * 100).toFixed(1)}% flag rate exceeds the 25% threshold.` });
    }
    if (unscoredVideos > 10) {
      alerts.push({ type: 'info', icon: 'hourglass_empty', message: `${unscoredVideos} videos are pending scoring across the group.` });
    }
  }

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />

      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
            <span className="text-sm font-bold text-slate-400 dark:text-slate-500 tracking-tight">Loading overview...</span>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
                {group?.name ?? 'Group Overview'}
              </h1>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                {[group?.oemBrand, group?.region].filter(Boolean).join(' · ') || 'Group overview and performance metrics'}
              </p>
            </div>

            {/* Alert Banners */}
            {alerts.length > 0 && (
              <div className="flex flex-col gap-3 mb-8">
                {alerts.map((alert, i) => (
                  <AlertBanner key={i} alert={alert} />
                ))}
              </div>
            )}

            {stats && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
                  <StatCard
                    label="Dealerships"
                    value={String(stats.tenantCount)}
                    icon="store"
                    accent="text-primary"
                    sub="Active locations"
                    onClick={() => navigate('/group-admin/tenants')}
                  />
                  <StatCard
                    label="Total Videos"
                    value={stats.totalVideos.toLocaleString()}
                    icon="play_circle"
                    accent="text-violet-600 dark:text-violet-400"
                    sub="Across all dealerships"
                    onClick={() => navigate('/group-admin/videos')}
                  />
                  <StatCard
                    label="Avg VQS Score"
                    value={stats.averageScore.toFixed(1)}
                    icon="leaderboard"
                    accent={scoreTextColor(stats.averageScore)}
                    sub={stats.averageScore >= 70 ? 'Proficient or above' : 'Needs improvement'}
                    onClick={() => navigate('/group-admin/benchmarks')}
                  />
                  <StatCard
                    label="Flag Rate"
                    value={`${(stats.complianceFlagRate * 100).toFixed(1)}%`}
                    icon="flag"
                    accent={stats.complianceFlagRate < 0.1 ? 'text-emerald-600 dark:text-emerald-400' : stats.complianceFlagRate < 0.25 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}
                    sub="Compliance flag rate"
                    onClick={() => navigate('/group-admin/videos?status=FLAGGED')}
                  />
                  <StatCard
                    label="Unscored Videos"
                    value={String(unscoredVideos)}
                    icon="hourglass_empty"
                    accent={unscoredVideos === 0 ? 'text-emerald-600 dark:text-emerald-400' : unscoredVideos > 10 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'}
                    sub={unscoredVideos === 0 ? 'All videos scored' : 'Pending or processing'}
                    onClick={() => navigate('/group-admin/videos?status=PENDING')}
                  />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Grade Distribution */}
                  <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6 flex flex-col">
                    <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-5">
                      Grade Distribution
                    </h2>

                    <div className="flex justify-around gap-2 mb-3 flex-1 min-h-[140px]">
                      {Object.entries(stats.gradeDistribution).map(([grade, count]) => {
                        const barPct = Math.max(5, Math.round((count / maxGradeCount) * 100));
                        return (
                          <div key={grade} className="flex-1 flex flex-col items-center">
                            <div className="flex-1 w-full relative">
                              <span
                                className="absolute w-full text-center text-[11px] font-black text-slate-600 dark:text-slate-300 leading-none"
                                style={{ bottom: `calc(${barPct}% + 5px)` }}
                              >
                                {count}
                              </span>
                              <div
                                className={`absolute bottom-0 w-full ${GRADE_COLORS[grade]?.bg ?? 'bg-slate-400'} transition-all duration-700`}
                                style={{ height: `${barPct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex justify-around gap-2 mb-5">
                      {Object.entries(stats.gradeDistribution).map(([grade, count]) => {
                        const pct = totalVideosWithGrade > 0 ? Math.round((count / totalVideosWithGrade) * 100) : 0;
                        return (
                          <div key={grade} className="flex-1 flex flex-col items-center gap-0.5">
                            <span className={`text-sm font-black ${GRADE_COLORS[grade]?.text ?? 'text-slate-400'}`}>{grade}</span>
                            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-600">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 grid grid-cols-5 gap-1">
                      {[
                        { grade: 'A', range: '85–100' },
                        { grade: 'B', range: '70–84' },
                        { grade: 'C', range: '55–69' },
                        { grade: 'D', range: '40–54' },
                        { grade: 'F', range: '0–39' },
                      ].map(({ grade, range }) => (
                        <div key={grade} className="flex flex-col items-center gap-0.5">
                          <div className={`w-2 h-2 rounded-full ${GRADE_COLORS[grade]?.bg ?? 'bg-slate-400'}`} />
                          <span className="text-[9px] font-bold text-slate-400">{range}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Signal Averages */}
                  <div className="lg:col-span-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6">
                    <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-6">
                      Signal Averages
                    </h2>
                    <div className="space-y-3">
                      {Object.entries(stats.signalAverages)
                        .map(([signal, avg]) => {
                          const score = Math.min(100, avg);
                          return (
                            <div key={signal} className="flex items-center gap-3 group">
                              <div className="flex items-center gap-2 w-40 shrink-0">
                                <span className={`material-symbols-outlined text-[16px] ${scoreTextColor(score)} opacity-70`}>
                                  {SIGNAL_ICONS[signal] ?? 'analytics'}
                                </span>
                                <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 truncate">
                                  {SIGNAL_LABELS[signal] ?? signal.replace(/([A-Z])/g, ' $1').trim().replace(/^./, (c) => c.toUpperCase())}
                                </span>
                              </div>
                              <div className="flex-1 bg-slate-100 dark:bg-slate-800 h-2 overflow-hidden">
                                <div
                                  className={`${scoreColor(score)} h-2 transition-all duration-700`}
                                  style={{ width: `${score}%` }}
                                />
                              </div>
                              <span className={`text-xs font-black w-8 text-right tabular-nums ${scoreTextColor(score)}`}>
                                {score.toFixed(0)}
                              </span>
                            </div>
                          );
                        })}
                    </div>

                    <div className="mt-5 pt-4 border-t border-slate-50 dark:border-slate-800 flex items-center gap-4">
                      {[
                        { color: 'bg-emerald-500', label: '80+' },
                        { color: 'bg-primary', label: '60–79' },
                        { color: 'bg-amber-400', label: '40–59' },
                        { color: 'bg-red-500', label: '<40' },
                      ].map(({ color, label }) => (
                        <div key={label} className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 ${color}`} />
                          <span className="text-[10px] font-bold text-slate-400">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default GroupAdminDashboardPage;
